/** Run projection. Read ownership is separate from server mutation outcome. */
import { useState, useCallback, useMemo, useRef, useEffect, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import {
  getCachedRun, getCachedRuns, getMyRuns, getOfflineDraftRun, getOfflineDraftRuns,
  getRun as requestRun, getRuns, isOfflineDraftRun,
} from '@/api/runs';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';

export const mergeOfflineRuns = (cachedRuns = [], offlineDraftRuns = []) => {
  const cachedCloudRuns = cachedRuns.filter(run => !isOfflineDraftRun(run));
  const cachedIds = new Set(cachedCloudRuns.map(run => run.id));
  const uniqueOfflineDraftRuns = offlineDraftRuns.filter(run => !cachedIds.has(run.id));
  return [...cachedCloudRuns, ...uniqueOfflineDraftRuns];
};

const emptyView = {
  runs: [], selectedRunId: null, loadingRuns: false, hasLoadedRuns: false, runsError: null,
};

const useRunsStore = (selectedDateString, { driverMode = false, offline = false } = {}) => {
  const revision = useSyncExternalStore(
    subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision,
  );
  const scopeKey = useMemo(() => ({ revision, selectedDateString, driverMode, offline }),
    [revision, selectedDateString, driverMode, offline]);
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const cycleRef = useRef(null);
  const [view, setView] = useState(emptyView);
  const owns = useCallback(cycle => Boolean(cycle?.active && cycleRef.current === cycle
    && currentScope.current === cycle.scope && cycle.scope.revision === getActiveSessionRevision()), []);
  const publish = useCallback(cycle => {
    if (owns(cycle)) setView({ ...cycle.view, scope: cycle.scope });
  }, [owns]);
  const getCycle = useCallback(() => {
    const cycle = cycleRef.current;
    return owns(cycle) && cycle.scope === scopeKey ? cycle : null;
  }, [owns, scopeKey]);

  const readList = useCallback(async (cycle, config, read = (execute, options) => execute(options)) => {
    if (!owns(cycle) || (!cycle.scope.selectedDateString && !cycle.scope.driverMode)) return false;
    const request = ++cycle.sequence;
    cycle.list = request;
    cycle.view.loadingRuns = true;
    cycle.view.runsError = null;
    publish(cycle);
    const current = () => owns(cycle) && cycle.list === request;
    try {
      const { selectedDateString: date, driverMode: driver, offline: local } = cycle.scope;
      let incoming;
      if (driver) incoming = await read(options => options ? getMyRuns(date, options) : getMyRuns(date), config);
      else if (local) {
        const [cached, drafts] = await Promise.all([
          read(options => options ? getCachedRuns(date, options) : getCachedRuns(date), config),
          read(options => options ? getOfflineDraftRuns(date, options) : getOfflineDraftRuns(date), config),
        ]);
        incoming = mergeOfflineRuns(cached, drafts);
      } else incoming = await read(options => options ? getRuns(date, options) : getRuns(date), config);
      if (!current()) return false;
      if (!Array.isArray(incoming) || incoming.some(run => !run || typeof run.id !== 'string')) throw new Error('Invalid run list');
      const previous = new Map(cycle.view.runs.map(run => [run.id, run]));
      const accepted = new Map();
      incoming.forEach(run => {
        // Local deletions leave a receipt too, preventing resurrection by an older read.
        if (cycle.localWrites.get(run.id) > request) {
          if (previous.has(run.id)) accepted.set(run.id, previous.get(run.id));
        } else {
          accepted.set(run.id, cycle.writes.get(run.id) > request && previous.has(run.id)
            ? previous.get(run.id) : run);
          cycle.writes.set(run.id, Math.max(request, cycle.writes.get(run.id) || 0));
        }
      });
      previous.forEach((run, id) => {
        if (accepted.has(id)) return;
        if (cycle.localWrites.get(id) > request) accepted.set(id, run);
        else cycle.writes.set(id, request);
      });
      // A local reorder or creation after this read began retains its ordering.
      const order = cycle.orderWrite > request
        ? [...previous.keys(), ...accepted.keys()] : [...accepted.keys()];
      cycle.view.runs = [...new Set(order)].filter(id => accepted.has(id)).map(id => accepted.get(id));
      if (cycle.view.selectedRunId && !accepted.has(cycle.view.selectedRunId)) {
        cycle.view.selectedRunId = null;
        cycle.selection += 1;
      }
      // Driver lists are complete assigned-run projections; selection does not
      // require the dispatcher's separate detail endpoint or mutation workflow.
      if (cycle.scope.driverMode && !cycle.view.selectedRunId && cycle.view.runs.length) {
        cycle.view.selectedRunId = cycle.view.runs[0].id;
        cycle.selection += 1;
      }
      cycle.view.hasLoadedRuns = true;
      publish(cycle);
      return true;
    } catch (error) {
      if (!current()) return false;
      console.error('Error fetching runs:', error);
      const localDesktop = window.lvbDesktop
        && window.lvbDesktop.backendUrl === window.lvbDesktop.localBackendUrl;
      cycle.view.runsError = localDesktop
        ? 'Cached runs are unavailable on this computer. Review offline services in Settings → Devices.'
        : 'Failed to load runs';
      if (!config) toast.error(cycle.view.runsError);
      return false;
    } finally {
      if (current()) {
        cycle.view.loadingRuns = false;
        publish(cycle);
      }
    }
  }, [owns, publish]);

  useEffect(() => {
    const cycle = {
      scope: scopeKey, active: true, sequence: 0, selection: 0,
      writes: new Map(), localWrites: new Map(), details: new Map(), orderWrite: 0,
      view: { ...emptyView, runs: [] },
    };
    cycleRef.current = cycle;
    publish(cycle);
    void readList(cycle);
    return () => { cycle.active = false; };
  }, [scopeKey, publish, readList]);

  const fetchRuns = useCallback(async (config, read) => {
    const cycle = getCycle();
    return cycle ? readList(cycle, config, read) : false;
  }, [getCycle, readList]);

  // Capture at the start of external work (for example, opening a delivery
  // form), not when that work finishes. A replayed lifecycle with an identical
  // scope must not inherit the retired operation's read continuation.
  const captureRunRefresh = useCallback(() => {
    const cycle = getCycle();
    return async () => {
      if (owns(cycle)) await readList(cycle);
    };
  }, [getCycle, owns, readList]);

  const fetchRunDetails = useCallback(async (runId, config, read = (execute, options) => execute(options)) => {
    const cycle = getCycle();
    const existing = cycle?.view.runs.find(run => run.id === runId);
    if (!existing) return null;
    const request = ++cycle.sequence;
    cycle.details.set(runId, request);
    const current = () => owns(cycle) && cycle.details.get(runId) === request
      && !(cycle.writes.get(runId) > request)
      && cycle.view.runs.some(run => run.id === runId);
    try {
      const requestDetails = cycle.scope.offline
        ? (isOfflineDraftRun(existing) ? getOfflineDraftRun : getCachedRun) : requestRun;
      const details = await read(options => options ? requestDetails(runId, options) : requestDetails(runId), config);
      if (!current()) return null;
      if (!details || details.id !== runId) throw new Error('Invalid run details');
      cycle.view.runs = cycle.view.runs.map(run => run.id === runId ? { ...run, ...details } : run);
      cycle.writes.set(runId, request);
      publish(cycle);
      return details;
    } catch (error) {
      if (current()) {
        console.error('Error fetching run details:', error);
        if (!config) toast.error('Failed to load run details');
      }
      return null;
    }
  }, [getCycle, owns, publish]);

  const setRuns = useCallback(update => {
    const cycle = getCycle();
    if (!cycle) return;
    const previous = cycle.view.runs;
    // Callers supply pure transforms. Execute once against the authoritative snapshot,
    // not inside React's replayable state updater.
    const next = typeof update === 'function' ? update(previous) : update;
    const before = new Map(previous.map(run => [run.id, run]));
    const after = new Map(next.map(run => [run.id, run]));
    const receipt = ++cycle.sequence;
    new Set([...before.keys(), ...after.keys()]).forEach(id => {
      if (before.get(id) !== after.get(id)) {
        cycle.writes.set(id, receipt);
        cycle.localWrites.set(id, receipt);
      }
    });
    if (previous.map(run => run.id).join('\0') !== next.map(run => run.id).join('\0')) {
      cycle.orderWrite = receipt;
    }
    cycle.view.runs = next;
    publish(cycle);
  }, [getCycle, publish]);

  const setSelectedRunId = useCallback(update => {
    const cycle = getCycle();
    if (!cycle) return;
    const next = typeof update === 'function' ? update(cycle.view.selectedRunId) : update;
    // Conditional command cleanup that changes nothing is not user intent.
    // An explicit same-ID selection still advances the intent receipt.
    if (typeof update === 'function' && next === cycle.view.selectedRunId) return;
    cycle.view.selectedRunId = next;
    cycle.selection += 1;
    publish(cycle);
  }, [getCycle, publish]);
  const clearSelectedRun = useCallback(() => setSelectedRunId(null), [setSelectedRunId]);
  const captureSelection = useCallback(() => {
    const cycle = getCycle();
    return cycle ? { cycle, selection: cycle.selection } : null;
  }, [getCycle]);
  const isSelectionCurrent = useCallback(token => Boolean(token && getCycle() === token.cycle
    && token.selection === token.cycle.selection), [getCycle]);
  const isScopeCurrent = useCallback(() => Boolean(getCycle()), [getCycle]);
  const getRun = useCallback(id => getCycle()?.view.runs.find(run => run.id === id) || null, [getCycle]);
  // Before the scope effect publishes, never display a retired list or a
  // misleading successful-empty state for the newly requested scope.
  const visible = view.scope === scopeKey ? view : {
    ...emptyView, loadingRuns: Boolean(selectedDateString || driverMode),
  };
  const selectedRun = visible.runs.find(run => run.id === visible.selectedRunId) || null;
  return {
    runs: visible.runs, selectedRun, selectedRunId: visible.selectedRunId, loadingRuns: visible.loadingRuns,
    hasLoadedRuns: visible.hasLoadedRuns, runsError: visible.runsError,
    setRuns, setSelectedRunId, fetchRuns, captureRunRefresh, fetchRunDetails, clearSelectedRun,
    captureSelection, isSelectionCurrent, isScopeCurrent, getRun, scopeKey,
  };
};

export default useRunsStore;
